import {makeAutoObservable} from "mobx";
import {api, catchApi} from "../../api";
import {validationStore} from "../ValidationStore";
import {PrinterDetailDto, PrinterDto, PrinterTypeDto} from "../../api/pms";
import {PmsFormData} from "../../components/page/pms/PmsForm";
import {equals} from "../../utils";

class PmsDetailStore {
    get loading(): boolean {
        return this._loading;
    }

    private set loading(value: boolean) {
        this._loading = value;
    }
    get type(): PrinterTypeDto | undefined {
        return this._type;
    }

    set type(value: PrinterTypeDto | undefined) {
        this._type = value;
    }
    get name(): string | undefined {
        return this._name;
    }

    set name(value: string | undefined) {
        this._name = value;
    }
    get displayName(): string | undefined {
        return this._displayName;
    }

    set displayName(value: string | undefined) {
        this._displayName = value;
    }

    get data(): PrinterDetailDto | undefined {
        return this._data;
    }

    set data(value: PrinterDetailDto | undefined) {
        this._data = value;
    }

    get id(): string | undefined {
        return this._id;
    }

    set id(value: string | undefined) {
        this._id = value;
    }

    get valid(): boolean {
        return this._valid;
    }

    set valid(value: boolean) {
        this._valid = value;
    }

    get dirty(): boolean {
        const current = this.build()
        return !equals(this.data?.data, current)
    }

    private _valid: boolean = false
    private _loading: boolean = false

    private _id: string | undefined
    private _data: PrinterDetailDto | undefined

    private _displayName: string | undefined
    private _name: string | undefined
    private _type: PrinterTypeDto | undefined = PrinterTypeDto.DOCUMENT

    constructor() {
        makeAutoObservable(this)
    }

    async load(): Promise<PrinterDetailDto | undefined> {
        this.loading = true
        const id = this.id!!
        const result = await catchApi(async () => await api.pms.api.getPrinter(id))

        this.data = result?.data

        this.reset()
        this.loading = false

        return result?.data
    }

    async save() {
        this.loading = true
        const id = this.id!!
        const result = await catchApi(async () => await api.pms.api.updatePrinter(id, {
            audit: this.data!.audit,
            data: this.build()
        }))

        this.data = result?.data
        this.reset()
        this.loading = false
    }

    get formData(): PmsFormData {
        const {
            name,
            displayName,
            type,
        } = this
        return {
            name,
            displayName,
            type
        }
    }

    set formData(data: PmsFormData) {
        const {
            name,
            displayName,
            type,
        } = data
        this.name = name
        this.displayName = displayName
        this.type = type
    }

    public build(): PrinterDto {
        const {
            name,
            displayName,
            type
        } = this
        return {
            name: name || '',
            displayName: displayName || '',
            type: type || PrinterTypeDto.DOCUMENT
        }
    }

    async validate() {
        this.valid = false
        const dto = this.build()
        await validationStore.validate(api.pms.api.validatePrinter, dto)

        this.valid = Boolean(validationStore.valid)
    }

    reset() {
        const {data} = this.data || {}
        if (data) {
            this.name = data.name
            this.displayName = data.displayName
            this.type = data.type
            this.valid = true
        }
    }
}

const pmsDetailStore = new PmsDetailStore()

export {
    pmsDetailStore
}
